import { TextField, Button, Stack, Typography, Box } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';
import { styledTextField } from '@old/styles/generic-styles';
import { PATH } from '@old/data/core/paths';
import { useService } from '@old/hooks/use-service';
import { useState, useEffect } from 'react';
import { useGlobalStore } from '@old/global-store/use-store';
import { ToastAction } from '@old/ui/toast/toast-action';
import CircularProgress from '@mui/material/CircularProgress';
import { verifyCredentialService } from '@old/services/domain/auth/verify-auth-credential';
import i18n from '../../../libs/language';

type VerifyCrendentialBody = Parameters<typeof verifyCredentialService>[0]['body'];

export function VerifyResetAccountForm() {
  const { email: storeEmail, setLanguage } = useGlobalStore();
  const [searchParams] = useSearchParams();

  // URL params take priority over internal store state
  const paramEmail = searchParams.get('email') ?? '';
  const paramCode = searchParams.get('code');
  const paramLang = searchParams.get('lang');

  const resolvedEmail = paramEmail || storeEmail || '';

  // Apply language from URL param (no API call — public page, no session yet)
  useEffect(() => {
    const lang = paramLang === 'pt' ? 'pt' : paramLang === 'es' ? 'es' : null;
    if (lang) {
      i18n.locale = lang;
      setLanguage(lang);
      localStorage.setItem('LANGUAGE_PREFERENCE', lang);
    }
  }, [paramLang]);

  const [formVerify, setFormVerify] = useState<VerifyCrendentialBody>({
    username: resolvedEmail,
    verification_code: paramCode !== null ? Number(paramCode) : null,
  });

  const navigate = useNavigate();

  const {
    trigger: sendVerify,
    isError,
    isLoading,
    error,
  } = useService(verifyCredentialService, {
    onSuccess: () => {
      navigate(`${PATH.RESET}?email=${encodeURIComponent(resolvedEmail)}`, { replace: true });
    },
  });

  const errorData = error as { data?: { msg?: string } } | undefined;
  const isExpiredCode = errorData?.data?.msg === 'The verification code is no longer valid.';
  const errorKey = isExpiredCode
    ? 'app.public_pages.verify_reset_account_error_expired'
    : 'app.public_pages.verify_reset_account_error';

  const handleVerify = (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    sendVerify({ body: formVerify });
  };

  return (
    <Stack direction={'column'} spacing={{ xs: 3, md: 2 }} mt={2}>
      <Box width={'100%'} display={'flex'} flexDirection={'column'} alignItems={'center'}>
        <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ textWrap: 'balance' }}>
          {i18n.t('app.public_pages.verify_reset_account_title')}
        </Typography>
        <Typography fontSize={13} color="textSecondary" mt={1} textAlign="center">
          {i18n.t('app.public_pages.verify_reset_account_subtitle')}
        </Typography>
      </Box>
      <TextField
        required
        id="email"
        label={i18n.t('app.public_pages.email')}
        variant="outlined"
        disabled={resolvedEmail !== ''}
        size="small"
        defaultValue={resolvedEmail}
        sx={styledTextField}
        onChange={(e) => setFormVerify({ ...formVerify, username: resolvedEmail || e.target.value })}
      />
      <TextField
        required
        id="verification_code"
        label={i18n.t('app.public_pages.verify_register_code')}
        variant="outlined"
        size="small"
        defaultValue={paramCode ?? ''}
        sx={styledTextField}
        onChange={(e) => setFormVerify({ ...formVerify, verification_code: +e.target.value })}
      />
      <Button
        variant="contained"
        sx={{ width: '400px', color: 'white' }}
        disableElevation
        onClick={handleVerify}
        type="submit"
      >
        {isLoading ? <CircularProgress sx={{ marginLeft: '10px', color: 'white' }} size={24} /> : 'Enviar'}
      </Button>

      {isError && <ToastAction message={i18n.t(errorKey)} severity="error" />}
    </Stack>
  );
}
